import { Icon, type IconName } from '@web/components/ui/icons'
import { useNarrow } from '@web/lib/useNarrow'
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type PillOption = {
  value: string
  /** Full name shown in the menu. */
  label: string
  /** What the closed pill shows; defaults to `label`. */
  short?: string
  description?: string
  disabled?: boolean
  icon?: IconName
  /** The `PillGroup` this option sits under in a two-layer menu. */
  group?: string
}

/** The first layer of a two-layer menu: a model provider above its models. */
export type PillGroup = {
  value: string
  label: string
  description?: string
  icon?: IconName
}

/**
 * A chip-sized trigger that opens a menu with a description per option.
 * Shared by launch fields and status selectors; opens toward available space.
 * With two or more `groups`, the menu opens on the groups and drills into
 * one group's options, so a provider is chosen before its model.
 */
export function PillMenu({
  value,
  options,
  groups,
  onChange,
  disabled,
  pending = false,
  label,
  showLabel = false,
  tone = 'chip',
  align = 'right',
  valueLabel = 'short',
}: {
  value: string
  options: PillOption[]
  groups?: PillGroup[]
  onChange: (value: string) => void
  disabled?: boolean
  pending?: boolean
  /** Explains the pill itself (shown as the menu heading). */
  label: string
  /** Retains the accessible/menu label while hiding it in the closed trigger. */
  showLabel?: boolean
  tone?: 'chip' | 'accent' | 'field'
  align?: 'left' | 'right'
  /** Closed pills show the option's short name; rows in a sheet show the full one. */
  valueLabel?: 'short' | 'full'
}) {
  const [open, setOpen] = useState(false)
  // The group whose options are shown; null is the group layer itself.
  const [layer, setLayer] = useState<string | null>(null)
  const layered = (groups?.length ?? 0) > 1
  const shownGroup = layered
    ? groups?.find((group) => group.value === layer)
    : undefined
  // Phones open the choices in a bottom drawer instead of a floating menu.
  const narrow = useNarrow()
  const [position, setPosition] = useState<CSSProperties>({
    left: 8,
    bottom: 8,
    width: 288,
    maxHeight: 400,
  })
  const root = useRef<HTMLSpanElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const id = useId()
  const current = options.find((o) => o.value === value)
  const currentLabel = current?.label ?? value
  const accessibleLabel = showLabel ? label : `${label}: ${currentLabel}`
  const trigger = useRef<HTMLButtonElement>(null)
  const restoreAfterChange = useRef(false)
  useEffect(() => {
    if (open || disabled || !restoreAfterChange.current) return
    restoreAfterChange.current = false
    if (
      document.activeElement === document.body ||
      document.activeElement === trigger.current
    )
      trigger.current?.focus()
  }, [open, disabled, value])
  const dismiss = (restore = true) => {
    setOpen(false)
    setLayer(null)
    if (restore) trigger.current?.focus()
  }
  const place = () => {
    const bounds = trigger.current?.getBoundingClientRect()
    if (!bounds) return
    const width = Math.min(288, window.innerWidth - 16)
    const left = align === 'left' ? bounds.left : bounds.right - width
    const below = window.innerHeight - bounds.bottom - 16
    const above = bounds.top - 16
    const desired = Math.min(400, 36 + options.length * 64)
    const downward = below >= desired || below >= above
    setPosition({
      left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
      ...(downward
        ? { top: bounds.bottom + 8 }
        : { bottom: window.innerHeight - bounds.top + 8 }),
      width,
      maxHeight: Math.max(0, Math.min(400, downward ? below : above)),
    })
  }

  useEffect(() => {
    if (!open) return
    ;(
      menu.current?.querySelector<HTMLElement>(
        '[aria-selected="true"]:not(:disabled)',
      ) ??
      menu.current?.querySelector<HTMLElement>('[role="option"]:not(:disabled)')
    )?.focus()
  }, [open, layer])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (
        !root.current?.contains(e.target as Node) &&
        !menu.current?.contains(e.target as Node)
      )
        setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        dismiss()
      }
    }
    const onResize = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  return (
    <span ref={root} className="relative inline-flex min-w-0 max-w-full">
      <button
        ref={trigger}
        type="button"
        className={`${tone === 'field' ? 'new-session-pill' : 'chip'} pill-menu-trigger gap-1 ${tone === 'accent' ? 'bg-accent-soft text-fg' : ''} ${disabled ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
        disabled={disabled}
        aria-busy={pending}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        aria-label={accessibleLabel}
        title={accessibleLabel}
        data-value={value}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            place()
            setOpen(true)
          }
        }}
        onClick={() => {
          place()
          setLayer(null)
          setOpen((v) => !v)
        }}
      >
        {showLabel && <span className="pill-menu-label">{label}</span>}
        {current?.icon && <Icon name={current.icon} size={13} />}
        <span className="pill-menu-value">
          {valueLabel === 'full'
            ? (current?.label ?? value)
            : (current?.short ?? current?.label ?? value)}
        </span>
        <Icon
          name={pending ? 'loader' : 'chevronDown'}
          size={10}
          className={pending ? 'spin text-faint' : 'text-faint'}
        />
      </button>
      {open &&
        createPortal(
          <div
            className={narrow ? 'm-sheet-scrim' : 'contents'}
            onMouseDown={(event) => {
              if (narrow && event.target === event.currentTarget) dismiss()
            }}
          >
            <div
              ref={menu}
              id={id}
              role="listbox"
              aria-label={label}
              onKeyDown={(event) => {
                const choices = Array.from(
                  menu.current?.querySelectorAll<HTMLButtonElement>(
                    '[role="option"]:not(:disabled)',
                  ) ?? [],
                )
                const at = choices.indexOf(
                  document.activeElement as HTMLButtonElement,
                )
                if (
                  ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
                ) {
                  event.preventDefault()
                  const index =
                    event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? choices.length - 1
                        : (at +
                            (event.key === 'ArrowDown' ? 1 : -1) +
                            choices.length) %
                          choices.length
                  choices[index]?.focus()
                } else if (event.key === 'Tab') dismiss()
                else if (event.key === 'ArrowLeft' && shownGroup) {
                  event.preventDefault()
                  setLayer(null)
                }
              }}
              className={
                narrow
                  ? 'pill-menu m-sheet m-pill-sheet'
                  : 'pill-menu fixed z-[100] overflow-y-auto rounded-card border border-line p-1 shadow-card'
              }
              style={narrow ? undefined : position}
            >
              {narrow ? (
                <>
                  <div className="m-sheet-grabber" aria-hidden="true" />
                  <div className="m-sheet-heading">
                    <button
                      type="button"
                      className="m-circle"
                      aria-label={`Close ${label}`}
                      onClick={() => dismiss()}
                    >
                      <Icon name="close" size={17} />
                    </button>
                    <div className="m-sheet-heading-copy">
                      <h2>{label}</h2>
                    </div>
                    <span className="m-sheet-heading-spacer" />
                  </div>
                </>
              ) : (
                <div className="label px-2 pb-1 pt-1.5">{label}</div>
              )}
              <div className={narrow ? 'm-sheet-body' : 'contents'}>
                {shownGroup && (
                  <button
                    type="button"
                    className={
                      narrow
                        ? 'pill-menu-back m-row'
                        : 'pill-menu-back flex w-full items-center gap-1 rounded-control px-2 py-1.5 text-left text-[12.5px] font-medium text-fg'
                    }
                    aria-label={`Back to all ${label.toLowerCase()} providers`}
                    onClick={() => setLayer(null)}
                  >
                    <Icon name="chevronLeft" size={12} className="text-muted" />
                    {shownGroup.label}
                  </button>
                )}
                <div className={narrow ? 'm-rowgroup-list' : 'contents'}>
                  {layered && !shownGroup
                    ? groups?.map((group) => {
                        const members = options.filter(
                          (o) => o.group === group.value,
                        )
                        const chosen = members.find((o) => o.value === value)
                        const selected = !!chosen
                        return (
                          <button
                            key={group.value}
                            type="button"
                            role="option"
                            aria-label={group.label}
                            aria-selected={selected}
                            aria-haspopup="listbox"
                            tabIndex={selected ? 0 : -1}
                            disabled={!members.length}
                            className={
                              narrow
                                ? `pill-menu-option m-row m-pill-option ${selected ? 'is-selected' : ''}`
                                : `pill-menu-option flex w-full items-start gap-2 rounded-control px-2 py-1.5 text-left transition-colors ${
                                    selected ? 'bg-accent-soft' : ''
                                  } disabled:cursor-not-allowed disabled:opacity-50`
                            }
                            onClick={() => setLayer(group.value)}
                          >
                            {group.icon && (
                              <Icon
                                name={group.icon}
                                size={16}
                                className="mt-0.5 text-muted"
                              />
                            )}
                            <span className="min-w-0 flex-1">
                              <span className="block text-[12.5px] font-medium text-fg">
                                {group.label}
                              </span>
                              <span className="block text-[11px] leading-snug text-muted">
                                {chosen?.label ??
                                  group.description ??
                                  `${members.length} ${members.length === 1 ? 'model' : 'models'}`}
                              </span>
                            </span>
                            <span className="mt-0.5 shrink-0 text-faint">
                              <Icon name="chevronRight" size={12} />
                            </span>
                          </button>
                        )
                      })
                    : options
                        .filter(
                          (o) => !shownGroup || o.group === shownGroup.value,
                        )
                        .map((o) => {
                          const selected = o.value === value
                          return (
                            <button
                              key={o.value}
                              type="button"
                              role="option"
                              aria-label={o.label}
                              aria-describedby={
                                o.description
                                  ? `${id}-${o.value}-description`
                                  : undefined
                              }
                              aria-selected={selected}
                              tabIndex={selected ? 0 : -1}
                              disabled={o.disabled}
                              className={
                                narrow
                                  ? `pill-menu-option m-row m-pill-option ${selected ? 'is-selected' : ''}`
                                  : `pill-menu-option flex w-full items-start gap-2 rounded-control px-2 py-1.5 text-left transition-colors ${
                                      selected ? 'bg-accent-soft' : ''
                                    } disabled:cursor-not-allowed disabled:opacity-50`
                              }
                              onClick={() => {
                                dismiss()
                                if (!selected) {
                                  restoreAfterChange.current = true
                                  onChange(o.value)
                                }
                              }}
                            >
                              {o.icon && (
                                <Icon
                                  name={o.icon}
                                  size={16}
                                  className="mt-0.5 text-muted"
                                />
                              )}
                              <span className="min-w-0 flex-1">
                                <span className="block text-[12.5px] font-medium text-fg">
                                  {o.label}
                                </span>
                                {o.description && (
                                  <span
                                    id={`${id}-${o.value}-description`}
                                    className="block text-[11px] leading-snug text-muted"
                                  >
                                    {o.description}
                                  </span>
                                )}
                              </span>
                              <span className="mt-0.5 w-3 shrink-0 text-accent">
                                {selected && <Icon name="check" size={12} />}
                              </span>
                            </button>
                          )
                        })}
                </div>
              </div>
            </div>
          </div>,
          root.current?.closest('[role="dialog"]') ?? document.body,
        )}
    </span>
  )
}
